import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Mail, Phone, Building, MapPin, FileText, TrendingUp, DollarSign, Calendar, Link, Copy, Check, Ticket, ExternalLink, Plus, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "sonner";
import { ClientSubscriptionsPanel } from "@/components/marketing/email/ClientSubscriptionsPanel";
import { CreateAgentCodeDialog } from "@/components/CreateAgentCodeDialog";
import { acquisitionSourceLabel } from "@/components/marketing/campaigns/campaignConstants";

interface Client {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  country: string | null;
  notes: string | null;
  created_at: string | null;
  access_token?: string | null;
  partner_type?: string | null;
  acquisition_source?: string | null;
  acquisition_campaign_id?: string | null;
  acquisition_notes?: string | null;
}

interface ViewClientDialogProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewClientDialog({ client, open, onOpenChange }: ViewClientDialogProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<'AGENT' | 'CONCIERGE'>('AGENT');

  const handleCopyPortalLink = async () => {
    if (!client?.access_token) return;
    const portalUrl = `${window.location.origin}/portal/client/${client.access_token}`;
    await navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    toast.success("Portal link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLeadClick = (leadId: string) => {
    onOpenChange(false);
    navigate(`/lead/${leadId}`);
  };

  const [scope, setScope] = useState<"liaison" | "company">("liaison");
  const LEAD_COLS = "id, title, status, quoted_price, currency, start_date, end_date, destination, client_name, client_liaison, client_id, group_reference";

  // Company match key: drop generic suffixes so "Lazy Lizard" and "Lazy Lizard Travel" match
  const companyKey = (client?.company || "")
    .toLowerCase()
    .replace(/[%,()_]/g, " ")
    .replace(/\b(travel|tours?|safaris?|pty|ltd|limited|cc|inc|llc|group)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Leads for this liaison: linked by client record, or naming this person as liaison
  const { data: leads } = useQuery({
    queryKey: ["client-leads", client?.id, client?.name],
    queryFn: async () => {
      if (!client) return [];
      const name = client.name.replace(/[%,()]/g, " ").trim();
      const { data, error } = await supabase
        .from("leads")
        .select(LEAD_COLS)
        .or(`client_id.eq.${client.id}${name ? `,client_liaison.ilike.${name}` : ""}`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!client && open,
  });

  // All leads for the company, regardless of liaison
  const { data: companyLeads } = useQuery({
    queryKey: ["company-leads", companyKey],
    queryFn: async () => {
      const { data: siblings } = await supabase
        .from("clients")
        .select("id")
        .ilike("company", `%${companyKey}%`);
      const ids = (siblings || []).map((s: any) => s.id);
      const filters = [`client_name.ilike.%${companyKey}%`];
      if (ids.length) filters.push(`client_id.in.(${ids.join(",")})`);
      const { data, error } = await supabase
        .from("leads")
        .select(LEAD_COLS)
        .or(filters.join(","))
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!client && open && companyKey.length >= 3,
  });

  const ownIds = new Set((leads || []).map((l: any) => l.id));
  const otherCompanyLeads = (companyLeads || []).filter((l: any) => !ownIds.has(l.id));
  const shownLeads = scope === "company" ? [...(leads || []), ...otherCompanyLeads] : leads;

  // Fetch agent_codes linked to this client for the Partner Profile card
  const { data: partnerCodes } = useQuery({
    queryKey: ["client-agent-codes", client?.id],
    queryFn: async () => {
      if (!client) return [];
      const { data, error } = await supabase
        .from("agent_codes")
        .select("id, code, agent_type, discount_percentage, commission_percentage, is_active, current_uses")
        .eq("client_id", client.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!client && open && !!client?.partner_type,
  });

  if (!client) return null;

  const openCreateCode = (type: 'AGENT' | 'CONCIERGE' = 'AGENT') => {
    setCreateType(type);
    setCreateOpen(true);
  };

  const prefill = {
    name: client.name,
    email: client.email,
    company: client.company,
    country: client.country,
    agentType: createType,
  };

  const totalValue = leads?.reduce((sum, lead) => sum + (lead.quoted_price || 0), 0) || 0;
  const leadCount = leads?.length || 0;

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "confirmed":
      case "completed":
        return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
      case "quote_sent":
      case "quote_accepted":
        return "bg-blue-500/10 text-blue-600 border-blue-500/20";
      case "cancelled":
      case "aborted":
        return "bg-red-500/10 text-red-600 border-red-500/20";
      default:
        return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl">{client.name}</DialogTitle>
              <DialogDescription>
                Client since {client.created_at ? format(new Date(client.created_at), "MMMM yyyy") : "Unknown"}
              </DialogDescription>
            </div>
            {client.access_token && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyPortalLink}
                className="flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Link className="h-4 w-4" />
                    Copy Portal Link
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {client.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${client.email}`} className="text-primary hover:underline">
                      {client.email}
                    </a>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${client.phone}`} className="hover:underline">
                      {client.phone}
                    </a>
                  </div>
                )}
                {client.company && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{client.company}</span>
                  </div>
                )}
                {client.country && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{client.country}</span>
                  </div>
                )}
                {client.acquisition_source && (
                  <div className="flex items-center gap-2 text-sm">
                    <UserPlus className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Acquired via <Badge variant="secondary" className="ml-1">{acquisitionSourceLabel(client.acquisition_source)}</Badge>
                      {client.acquisition_notes && (
                        <span className="text-muted-foreground"> — {client.acquisition_notes}</span>
                      )}
                    </span>
                  </div>
                )}
                {!client.email && !client.phone && !client.company && !client.country && (
                  <p className="text-sm text-muted-foreground">No contact information available</p>
                )}
              </CardContent>
            </Card>

            {/* Partner Profile (Agents / Concierges) */}
            {client.partner_type && (
              <Card
                className="border-l-4"
                style={{
                  borderLeftColor:
                    client.partner_type === "CONCIERGE" ? "#d97706" : "#289db4",
                }}
              >
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    Partner Profile
                    <Badge
                      className="text-[10px] border-0 text-white ml-1"
                      style={{
                        backgroundColor:
                          client.partner_type === "CONCIERGE" ? "#d97706" : "#289db4",
                      }}
                    >
                      {client.partner_type}
                    </Badge>
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() =>
                        openCreateCode(
                          (client.partner_type === "CONCIERGE" ? "CONCIERGE" : "AGENT") as
                            | "AGENT"
                            | "CONCIERGE"
                        )
                      }
                    >
                      <Plus className="h-3 w-3" />
                      New code
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() => {
                        onOpenChange(false);
                        navigate("/promo-codes");
                      }}
                    >
                      Manage codes
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {partnerCodes && partnerCodes.length > 0 ? (
                    partnerCodes.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between p-2 rounded border bg-muted/30 text-sm"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-semibold">{c.code}</span>
                          <Badge variant="outline" className="text-xs">
                            {c.discount_percentage}% off
                          </Badge>
                          {c.commission_percentage ? (
                            <Badge variant="outline" className="text-xs">
                              {c.commission_percentage}% comm.
                            </Badge>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{c.current_uses || 0} uses</span>
                          <Badge
                            variant={c.is_active ? "default" : "secondary"}
                            className="text-[10px]"
                          >
                            {c.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No promo codes linked yet.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Make this client a partner (when no partner_type yet) */}
            {!client.partner_type && (
              <Card className="border-dashed">
                <CardContent className="pt-6 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Make this client a partner
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Generate an agent or concierge code prefilled with this client's details.
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      style={{ borderColor: "#289db4", color: "#289db4" }}
                      onClick={() => openCreateCode("AGENT")}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Agent code
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      style={{ borderColor: "#d97706", color: "#d97706" }}
                      onClick={() => openCreateCode("CONCIERGE")}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Concierge code
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span className="text-2xl font-bold">{leadCount}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-2xl font-bold">
                      {totalValue.toLocaleString("en-US", {
                        style: "currency",
                        currency: "ZAR",
                        minimumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                </CardContent>
              </Card>
            </div>

            {/* Email Subscriptions */}
            {(() => {
              const [firstName, ...rest] = (client.name || "").trim().split(/\s+/);
              const lastName = rest.join(" ");
              return (
                <ClientSubscriptionsPanel
                  clientEmail={client.email}
                  firstName={firstName || null}
                  lastName={lastName || null}
                />
              );
            })()}

            {/* Notes */}
            {client.notes && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Leads List */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium">
                    {scope === "company" ? `All leads for ${client.company}` : `${client.name}'s leads`}
                  </CardTitle>
                  {otherCompanyLeads.length > 0 && (
                    <div className="flex rounded-md border p-0.5">
                      <Button size="sm" variant={scope === "liaison" ? "secondary" : "ghost"} className="h-7 text-xs" onClick={() => setScope("liaison")}>
                        {client.name.split(" ")[0]} ({leads?.length || 0})
                      </Button>
                      <Button size="sm" variant={scope === "company" ? "secondary" : "ghost"} className="h-7 text-xs" onClick={() => setScope("company")}>
                        Whole company ({(leads?.length || 0) + otherCompanyLeads.length})
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {scope === "liaison" && otherCompanyLeads.length > 0 && (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed bg-muted/40 p-3 text-sm">
                    <span className="text-muted-foreground">
                      {client.company} has {otherCompanyLeads.length} other lead{otherCompanyLeads.length !== 1 ? "s" : ""} handled by other contacts. Show them?
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setScope("company")}>Show all</Button>
                  </div>
                )}
                {shownLeads && shownLeads.length > 0 ? (
                  shownLeads.map((lead: any) => (
                    <div
                      key={lead.id}
                      onClick={() => handleLeadClick(lead.id)}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                    >
                      <div className="space-y-1">
                        <p className="font-medium text-sm hover:text-primary">{lead.title}</p>
                        {lead.group_reference && (
                          <p className="text-xs text-foreground/80">Ref: {lead.group_reference}</p>
                        )}
                        {scope === "company" && !ownIds.has(lead.id) && lead.client_liaison && (
                          <p className="text-xs text-muted-foreground">Contact: {lead.client_liaison}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {lead.destination && <span>{lead.destination}</span>}
                          {lead.start_date && (
                            <>
                              <Separator orientation="vertical" className="h-3" />
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {format(new Date(lead.start_date), "MMM d, yyyy")}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {lead.quoted_price && (
                          <span className="text-sm font-medium">
                            {lead.quoted_price.toLocaleString("en-US", {
                              style: "currency",
                              currency: lead.currency || "ZAR",
                              minimumFractionDigits: 0,
                            })}
                          </span>
                        )}
                        <Badge variant="outline" className={getStatusColor(lead.status)}>
                          {lead.status?.replace(/_/g, " ") || "lead"}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No leads associated with this client
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
    <CreateAgentCodeDialog
      open={createOpen}
      onOpenChange={setCreateOpen}
      clientId={client.id}
      prefill={prefill}
    />
    </>
  );
}
